import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import T from 'i18n';
import NavBar from 'features/common/NavBar';
import Footer from 'features/common/Footer';
import withAuth from 'features/auth/withAuth';
import { useLocation, useParams } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HistoryIcon from '@mui/icons-material/History';
import { addMinutes, format, isAfter, isBefore, subSeconds } from 'date-fns';
import {
  selectAccessToken,
  selectCurrentCase,
  selectCurrentCaseId,
  selectFetchCasesPending,
  selectHubConnectionId,
  selectUserId,
  selectIdentityId,
  selectUserName,
} from 'common/selectors';
import { useUpdateUserSettings } from 'features/common/redux/hooks';
import Logger from 'utils/logger';
import { openNewBackgroundTab } from 'utils/browser';
import { useFetchRecentlyPresentedToday } from 'features/conference/redux/hooks';
import FullScreenButton from '../FullScreenButton';
import TranscriptLine, { EMPTY_HIGHLIGHTS } from './components/TranscriptLine';
import CommentsPanel from './components/CommentsPanel';
import type { CommentsFilter } from './components/CommentsPanel';
import FilterCommentsPanel from './components/FilterCommentsPanel';
import HighlightPopup from './components/HighlightPopup';
import ThemeSettingsMenu from './components/ThemeSettingsMenu';
import ExportMenu from './components/ExportMenu';
import useTextHighlights from './hooks/useTextHighlights';
import useDocFinderNavigation from './hooks/useDocFinderNavigation';
import useTranscriptSearch from './hooks/useTranscriptSearch';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { bootstrapRealTime, fetchEndedSessions, fetchSessionTranscripts } from './api';
import { useRealTimeTranscript, receiveRealTimeTranscriptMessage } from '../redux/realTimeTranscript';
import { formatTimestamp } from './utils/formatters';
import exportTranscriptPdf from './utils/exportTranscriptPdf';
import exportTranscriptTxt from './utils/exportTranscriptTxt';
import type {
  RealTimeTranscriptLine,
  OldTranscriptSession,
  TranscriptHighlight,
  Visibility,
  VisibilitySettings,
} from './types';
import { getRealTimeThemeClassName, normalizeRealTimeTheme } from './theme';
import type { RealTimeTheme } from './theme';
import FilterPresentedDocumentsBase, { defaultFilter } from '../FilterPresentedDocuments';

const FilterPresentedDocuments = FilterPresentedDocumentsBase as React.ComponentType<{
  documentsFilter: { from: Date | null; to: Date | null };
  setDocumentsFilter: React.Dispatch<React.SetStateAction<{ from: Date | null; to: Date | null }>>;
}>;

type RealTimeProps = {
  hearingRoomMode?: 'public' | 'private';
  children?: React.ReactNode;
};

type BootstrapLine = {
  lineId?: string;
  lineNumber?: number;
  pageNumber?: number;
  pageLineNumber?: number;
  version?: number;
  text?: string;
  lastUpdatedAtUtc?: string | null;
  _isOpen?: boolean;
};

type PresentedDocument = {
  viewed?: string;
  name?: string;
  id?: string | number;
  type?: string;
  presentedBy?: string;
};

type PresentedDocsFilter = {
  from: Date | null;
  to: Date | null;
};



const AUTO_SCROLL_THRESHOLD = 40;
const LIVE_WINDOW_MS = 15000;
const DOCS_AUTO_COLLAPSE_WIDTH = 780;

const commentsDefaultFilter: CommentsFilter = { from: null, to: null };


const isErrorResponse = (value: unknown): value is { error?: unknown } =>
  !!value && typeof value === 'object' && 'error' in value;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const hasLineIdentity = (line: Partial<RealTimeTranscriptLine>) =>
  line.lineId != null || line.lineNumber != null || line.pageLineNumber != null;

const hasCommentContent = (note: TranscriptHighlight) =>
  note.note.trim().length > 0 || (note.comments?.length ?? 0) > 0;

const getNoteLineIds = (note: TranscriptHighlight) => {
  const lineIds =
    note.selections && note.selections.length > 0
      ? note.selections.map((selection) => selection.lineId)
      : [note.lineId];

  return Array.from(new Set(lineIds.filter(Boolean)));
};

const normalizePresentedDocs = (response: unknown): PresentedDocument[] => {
  if (Array.isArray(response)) return response as PresentedDocument[];
  if (isPlainObject(response)) {
    const nested = response.data ?? response.results ?? response.items;
    if (Array.isArray(nested)) return nested as PresentedDocument[];
  }
  return [];
};

const extractBootstrapLines = (response: unknown): BootstrapLine[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response.filter(isPlainObject) as BootstrapLine[];
  if (isPlainObject(response)) {
    if (Array.isArray(response.finalizedLines)) {
      const lines = [...(response.finalizedLines as BootstrapLine[])];
      if (isPlainObject(response.openLine)) {
        lines.push({ ...(response.openLine as BootstrapLine), _isOpen: true });
      }
      return lines;
    }
    const nested = response.data ?? response.results ?? response.items;
    if (nested) return extractBootstrapLines(nested);
  }
  return [];
};

const normalizeBootstrapLines = (transcripts: BootstrapLine[]): RealTimeTranscriptLine[] => {
  if (!transcripts.length) return [];

  return transcripts
    .filter((entry) => entry.lineId != null || entry.lineNumber != null)
    .map((entry) => ({
      id: String(entry.lineId ?? entry.lineNumber),
      lineId: entry.lineId != null ? String(entry.lineId) : undefined,
      lineNumber: entry.lineNumber,
      pageNumber: entry.pageNumber,
      pageLineNumber: entry.pageLineNumber,
      version: entry.version,
      text: entry.text ?? '',
      finalizedAtUtc: entry.lastUpdatedAtUtc ?? null,
      timestamp: entry.lastUpdatedAtUtc ?? undefined,
      isFinal: !entry._isOpen,
    }))
    // Deduplicate by id: the server echoes the in-progress line in both
    // finalizedLines and openLine. Always keep the open version (isFinal:false)
    // so the cursor and animation are correct.
    .reduce<RealTimeTranscriptLine[]>((acc, line) => {
      const idx = acc.findIndex((l) => l.id === line.id);
      if (idx === -1) {
        acc.push(line);
      } else if (line.isFinal === false) {
        acc[idx] = line;
      }
      return acc;
    }, []);
};

const PresentedDocsPanel: React.FC<{
  caseId: string;
  notes: TranscriptHighlight[];
  onCommentNoteClick: (note: TranscriptHighlight) => void;
  onSubmitComment?: (highlightId: string, commentText: string) => Promise<boolean>;
  hideMyAnnotations: boolean;
  hideOthersAnnotations: boolean;
  onHideMyAnnotationsChange: (value: boolean) => void;
  onHideOthersAnnotationsChange: (value: boolean) => void;
}> = ({
  caseId,
  notes,
  onCommentNoteClick,
  onSubmitComment,
  hideMyAnnotations,
  hideOthersAnnotations,
  onHideMyAnnotationsChange,
  onHideOthersAnnotationsChange,
}) => {
  const { fetchRecentlyPresentedToday } = useFetchRecentlyPresentedToday();
  const [recentlyPresented, setRecentlyPresented] = useState<PresentedDocument[]>([]);
  const [documentsFilter, setDocumentsFilter] = useState<PresentedDocsFilter>(defaultFilter);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showDocsScrollbar, setShowDocsScrollbar] = useState(false);
  const [activeTab, setActiveTab] = useState<'comments' | 'docs'>('comments');
  const [commentsSearchQuery, setCommentsSearchQuery] = useState('');
  const [commentsFilter, setCommentsFilter] = useState<CommentsFilter>(commentsDefaultFilter);
  const [commentsFiltersOpen, setCommentsFiltersOpen] = useState(false);
  const [commentsFilterAnchor, setCommentsFilterAnchor] = useState<HTMLElement | null>(null);
  const docsScrollbarTimerRef = useRef<number | null>(null);
  const wasBelowAutoCollapseWidthRef = useRef(false);
  const loadPresentedDocuments = useCallback(async () => {
    if (!caseId) return;

    try {
      const response = await fetchRecentlyPresentedToday();
      const normalized = normalizePresentedDocs(response);
      setRecentlyPresented(normalized.slice().reverse());
    } catch (error) {
      Logger.ERROR('Failed to fetch recently presented documents', error);
    }
  }, [caseId, fetchRecentlyPresentedToday]);

  useEffect(() => {
    setRecentlyPresented([]);
  }, [caseId]);

  useEffect(() => {
    if (!caseId) return undefined;

    loadPresentedDocuments();
    const timer = window.setInterval(loadPresentedDocuments, 10000);
    return () => window.clearInterval(timer);
  }, [caseId, loadPresentedDocuments]);

  const filteredDocuments = useMemo(() => {
    const from = documentsFilter?.from ?? null;
    const to = documentsFilter?.to ?? null;
    if (!from || !to) return recentlyPresented;

    return recentlyPresented.filter((document) => {
      if (!document?.viewed) return false;
      const date = new Date(document.viewed);
      if (Number.isNaN(date.getTime())) return false;

      return isAfter(date, subSeconds(from, 1)) && isBefore(date, addMinutes(to, 1));
    });
  }, [documentsFilter?.from, documentsFilter?.to, recentlyPresented]);

  const hasCustomFilter = useMemo(() => {
    const from = documentsFilter?.from?.getTime?.();
    const to = documentsFilter?.to?.getTime?.();
    return (
      typeof from === 'number' &&
      typeof to === 'number' &&
      (from !== defaultFilter.from.getTime() || to !== defaultFilter.to.getTime())
    );
  }, [documentsFilter?.from, documentsFilter?.to]);

  const docsCount = filteredDocuments.length;
  const commentsCount = useMemo(
    () => notes.filter((note) => hasCommentContent(note)).length,
    [notes],
  );
  const activeCount = activeTab === 'comments' ? commentsCount : docsCount;

  const handleToggleFilters = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setFiltersOpen((prev) => !prev);
    setFilterAnchor(event.currentTarget);
  }, []);

  const handleCloseFilters = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const hasCustomCommentsFilter =
    commentsFilter.from !== null || commentsFilter.to !== null ||
    hideMyAnnotations || hideOthersAnnotations;

  const handleToggleCommentsFilters = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setCommentsFiltersOpen((prev) => !prev);
      setCommentsFilterAnchor(event.currentTarget);
    },
    [],
  );

  const handleCloseCommentsFilters = useCallback(() => {
    setCommentsFiltersOpen(false);
  }, []);

  const handleToggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      if (!prev) setFiltersOpen(false);
      return !prev;
    });
  }, []);

  const triggerDocsScrollbar = useCallback(() => {
    setShowDocsScrollbar(true);
    if (docsScrollbarTimerRef.current) {
      window.clearTimeout(docsScrollbarTimerRef.current);
    }
    docsScrollbarTimerRef.current = window.setTimeout(() => {
      setShowDocsScrollbar(false);
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (docsScrollbarTimerRef.current) {
        window.clearTimeout(docsScrollbarTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'docs') {
      setFiltersOpen(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncCollapsedForWidth = () => {
      const isBelowBreakpoint = window.innerWidth <= DOCS_AUTO_COLLAPSE_WIDTH;

      if (isBelowBreakpoint && !wasBelowAutoCollapseWidthRef.current) {
        setCollapsed(true);
      }

      wasBelowAutoCollapseWidthRef.current = isBelowBreakpoint;
    };

    syncCollapsedForWidth();
    window.addEventListener('resize', syncCollapsedForWidth);
    return () => window.removeEventListener('resize', syncCollapsedForWidth);
  }, []);

  const handleOpenDocument = useCallback(
    (documentId?: string | number) => {
      if (!caseId || documentId == null) return;
      openNewBackgroundTab(`/case/${caseId}/files/${documentId}`);
    },
    [caseId],
  );

  return (
    <aside className={classnames('real-time__docs', { 'is-collapsed': collapsed })}>
      <div className="real-time__docs-rail">
        <div className="real-time__docs-rail-top">
          {collapsed && (
            <Tooltip
              title={
                activeTab === 'comments'
                  ? `${activeCount} ${activeCount === 1 ? 'comment' : 'comments'}`
                  : `${activeCount} ${activeCount === 1 ? 'Presented Document' : 'Presented Documents'}`
              }
              arrow
              placement="left"
            >
              <span className="real-time__docs-rail-badge">
                {activeTab === 'comments' ? (
                  <CommentOutlinedIcon className="real-time__docs-rail-icon" fontSize="small" />
                ) : (
                  <DescriptionOutlinedIcon className="real-time__docs-rail-icon" fontSize="small" />
                )}
                <span className="real-time__docs-rail-count">{activeCount}</span>
              </span>
            </Tooltip>
          )}
        </div>
        <button
          type="button"
          className="real-time__docs-toggle"
          onClick={handleToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={T.translate('case.presentedDocsToggle', {
            defaultValue: collapsed ? 'Show presented documents' : 'Hide presented documents',
          })}
        >
          {collapsed ? <ChevronLeftRoundedIcon /> : <ChevronRightRoundedIcon />}
        </button>
      </div>
      <div className="real-time__docs-panel">
        <div className="real-time__docs-tabs" role="tablist" aria-label="Real-time sidebar tabs">
          <button
            type="button"
            role="tab"
            className={classnames('real-time__docs-tab', {
              'is-active': activeTab === 'comments',
            })}
            aria-selected={activeTab === 'comments'}
            onClick={() => setActiveTab('comments')}
          >
            <span>{T.translate('case.realTimeAnnotations', { defaultValue: 'Annotations' })}</span>
            <span className="real-time__docs-count">{commentsCount}</span>
          </button>
          <button
            type="button"
            role="tab"
            className={classnames('real-time__docs-tab', {
              'is-active': activeTab === 'docs',
            })}
            aria-selected={activeTab === 'docs'}
            onClick={() => setActiveTab('docs')}
          >
            <span>{T.translate('case.presentedDocs', { defaultValue: 'Presented Docs' })}</span>
            <span className="real-time__docs-count">{docsCount}</span>
          </button>
        </div>
        {activeTab === 'comments' ? (
          <>
            <div className="real-time__docs-header">
              <div className="real-time__docs-title">
                <span className="real-time__docs-title-text">
                  {T.translate('case.realTimeAnnotations', { defaultValue: 'Annotations' })}
                </span>
                <span className="real-time__docs-count">{commentsCount}</span>
              </div>
              <ClickAwayListener onClickAway={handleCloseCommentsFilters}>
                <div className="real-time__docs-actions">
                  <button
                    type="button"
                    className={classnames('real-time__docs-filter', {
                      'is-active': hasCustomCommentsFilter,
                    })}
                    onClick={handleToggleCommentsFilters}
                    aria-label={T.translate('filters.filters', { defaultValue: 'Filters' })}
                  >
                    <FilterListRoundedIcon fontSize="small" />
                    {hasCustomCommentsFilter && <span className="real-time__docs-filter-dot" />}
                  </button>
                  <Popper
                    id="comments-filter"
                    className="real-time__docs-popper"
                    open={Boolean(commentsFiltersOpen)}
                    anchorEl={commentsFilterAnchor}
                    disablePortal
                    placement="bottom-end"
                  >
                    <FilterCommentsPanel
                      commentsFilter={commentsFilter}
                      setCommentsFilter={setCommentsFilter}
                      hideMyAnnotations={hideMyAnnotations}
                      hideOthersAnnotations={hideOthersAnnotations}
                      onHideMyAnnotationsChange={onHideMyAnnotationsChange}
                      onHideOthersAnnotationsChange={onHideOthersAnnotationsChange}
                    />
                  </Popper>
                </div>
              </ClickAwayListener>
            </div>
            <div className="real-time__comments-search">
              <div className="real-time__comments-search-box">
                <SearchIcon className="real-time__comments-search-icon" fontSize="small" />
                <input
                  type="text"
                  className="real-time__comments-search-input"
                  placeholder={T.translate('case.realTimeSearchComments', {
                    defaultValue: 'Search',
                  })}
                  value={commentsSearchQuery}
                  onChange={(e) => setCommentsSearchQuery(e.target.value)}
                />
                {commentsSearchQuery && (
                  <button
                    type="button"
                    className="real-time__comments-search-clear"
                    onClick={() => setCommentsSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </button>
                )}
              </div>
            </div>
            <CommentsPanel
              notes={notes}
              isScrolling={showDocsScrollbar}
              onScroll={triggerDocsScrollbar}
              onNoteClick={onCommentNoteClick}
              onSubmitComment={onSubmitComment}
              searchQuery={commentsSearchQuery}
              commentsFilter={hasCustomCommentsFilter ? commentsFilter : undefined}
            />
          </>
        ) : (
          <>
            <div className="real-time__docs-header">
              <div className="real-time__docs-title">
                <span className="real-time__docs-title-text">
                  {T.translate('case.presentedDocs', { defaultValue: 'Presented Docs' })}
                </span>
                <span className="real-time__docs-count">{docsCount}</span>
              </div>
              <ClickAwayListener onClickAway={handleCloseFilters}>
                <div className="real-time__docs-actions">
                  <button
                    type="button"
                    className={classnames('real-time__docs-filter', {
                      'is-active': hasCustomFilter,
                    })}
                    onClick={handleToggleFilters}
                    aria-label={T.translate('filters.filters', { defaultValue: 'Filters' })}
                  >
                    <FilterListRoundedIcon fontSize="small" />
                    {hasCustomFilter && <span className="real-time__docs-filter-dot" />}
                  </button>
                  <Popper
                    id="presented-docs-filter"
                    className="real-time__docs-popper"
                    open={Boolean(filtersOpen)}
                    anchorEl={filterAnchor}
                    disablePortal
                    placement="bottom-end"
                  >
                    <FilterPresentedDocuments
                      documentsFilter={documentsFilter}
                      setDocumentsFilter={setDocumentsFilter}
                    />
                  </Popper>
                </div>
              </ClickAwayListener>
            </div>
            <div
              className={classnames('real-time__docs-list', { 'is-scrolling': showDocsScrollbar })}
              role="list"
              onScroll={triggerDocsScrollbar}
            >
              {filteredDocuments.length === 0 ? (
                <div className="real-time__docs-empty">
                  {T.translate('case.presentedDocsEmpty', { defaultValue: 'No documents yet.' })}
                </div>
              ) : (
                filteredDocuments.map(({ viewed, name, id, type, presentedBy }, index) => {
                  const viewedDate = viewed ? new Date(viewed) : null;
                  const hasViewedDate = viewedDate && !Number.isNaN(viewedDate.getTime());
                  const timeLabel = hasViewedDate ? format(viewedDate as Date, 'HH:mm') : '--:--';
                  const dateLabel = hasViewedDate
                    ? format(viewedDate as Date, 'dd/MM/yyyy')
                    : '--/--/----';
                  const fileType = type ? String(type).toUpperCase() : 'FILE';
                  const documentId = id ?? index;
                  const presentedByLabel = T.translate('case.presentedBy', {
                    defaultValue: 'Presented by',
                  });

                  const card = (
                    <button
                      key={documentId}
                      type="button"
                      className="real-time__docs-card"
                      onClick={() => handleOpenDocument(documentId)}
                      disabled={!caseId}
                    >
                      <div className="real-time__docs-id">{documentId}</div>
                      <div className="real-time__docs-name">{name || 'Untitled document'}</div>
                      <div className="real-time__docs-meta">
                        <span className="real-time__docs-type">{fileType}</span>
                        <span className="real-time__docs-time">
                          {timeLabel} · {dateLabel}
                        </span>
                      </div>
                    </button>
                  );

                  if (!presentedBy) return card;

                  return (
                    <Tooltip
                      key={documentId}
                      title={`${presentedByLabel}: ${presentedBy}`}
                      placement="top"
                    >
                      {card}
                    </Tooltip>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};

export const RealTime: React.FC<RealTimeProps> = ({ hearingRoomMode = 'public' }) => {
  const {
    lines,
    lastEventAt,
    sessionKey: reduxSessionKey,
    sessionRestartedAt,
    sessionNotificationType,
    restartSessionKey,
    noteNotifications,
    reindexRangePending,
    clear,
    clearReindexRange,
  } = useRealTimeTranscript();
  const dispatch = useDispatch();
  const { case: routeCaseId } = useParams<{ case: string }>();
  const location = useLocation<{ caseId?: string }>();
  const accessToken = useSelector(selectAccessToken);
  const fetchCasesPending = useSelector(selectFetchCasesPending);
  const fetchCaseDetailsPending = useSelector((state: any) => state.case.fetchCaseDetailsPending);
  const currentCase = useSelector(selectCurrentCase);
  const caseId = useSelector(selectCurrentCaseId);
  const hubConnectionId = useSelector(selectHubConnectionId);
  const userId = useSelector(selectUserId);
  const identityId = useSelector(selectIdentityId);
  const userName = useSelector(selectUserName);
  const { userSettings, updateUserSettings } = useUpdateUserSettings();
  const realTimeThemeSetting = (userSettings as any)?.realTimeTheme;
  const realTimeShowTimestamps = (userSettings as any)?.realTimeShowTimestamps;
  const realTimeHighlightVisibility = (userSettings as any)?.realTimeHighlightVisibility;
  const realTimeNoteVisibility = (userSettings as any)?.realTimeNoteVisibility;
  const realTimeTagVisibility = (userSettings as any)?.realTimeTagVisibility;

  const [bootstrapTrigger, setBootstrapTrigger] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [, setHasUnread] = useState(false);
  const [isScrolledAway, setIsScrolledAway] = useState(false);
  const [bootstrapTranscripts, setBootstrapTranscripts] = useState<BootstrapLine[]>([]);
  const [bootstrapReady, setBootstrapReady] = useState(false);
  const [bootstrapSessionKey, setBootstrapSessionKey] = useState<string | null>(null);
  const [showLogScrollbar, setShowLogScrollbar] = useState(false);
  const [goToPageInput, setGoToPageInput] = useState('');
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [navigatedHighlightId, setNavigatedHighlightId] = useState<string | null>(null);
  const [hideMyAnnotations, setHideMyAnnotations] = useState(false);
  const [hideOthersAnnotations, setHideOthersAnnotations] = useState(false);
  const persistedTheme = useMemo(
    () => normalizeRealTimeTheme(realTimeThemeSetting),
    [realTimeThemeSetting],
  );
  const [theme, setTheme] = useState<RealTimeTheme>(persistedTheme);
  const [isThemeSaving, setIsThemeSaving] = useState(false);
  const persistedShowTimestamps = realTimeShowTimestamps !== false;
  const [showTimestamps, setShowTimestamps] = useState(persistedShowTimestamps);
  const [isTimestampsSaving, setIsTimestampsSaving] = useState(false);
  const normalizeVisibility = (v: unknown): Visibility => (v === 'team' ? 'team' : 'private');
  const persistedVisibilitySettings = useMemo<VisibilitySettings>(
    () => ({
      highlight: normalizeVisibility(realTimeHighlightVisibility),
      note: normalizeVisibility(realTimeNoteVisibility),
      tag: normalizeVisibility(realTimeTagVisibility),
    }),
    [realTimeHighlightVisibility, realTimeNoteVisibility, realTimeTagVisibility],
  );
  const [visibilitySettings, setVisibilitySettings] = useState<VisibilitySettings>(
    persistedVisibilitySettings,
  );
  const [isVisibilitySaving, setIsVisibilitySaving] = useState(false);
  const [isOldTranscriptMode, setIsOldTranscriptMode] = useState(false);
  const [oldTranscriptLines, setOldTranscriptLines] = useState<BootstrapLine[]>([]);
  const [oldSessionKey, setOldSessionKey] = useState<string | null>(null);
  const [isLoadingOldTranscript, setIsLoadingOldTranscript] = useState(false);
  const [oldTranscriptSessions, setOldTranscriptSessions] = useState<OldTranscriptSession[]>([]);
  const [selectedOldSession, setSelectedOldSession] = useState<OldTranscriptSession | null>(null);
  const [isLoadingSessionLines, setIsLoadingSessionLines] = useState(false);
  const [, setSessionsMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [oldTranscriptsDropdownAnchor, setOldTranscriptsDropdownAnchor] =
    useState<HTMLButtonElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isSelectingRef = useRef(false);
  const previousCountRef = useRef(0);
  const previousEventRef = useRef<number | null>(null);
  const bootstrapCompletedRef = useRef<string | null>(null);
  const bootstrapInFlightRef = useRef<string | null>(null);
  const logScrollbarTimerRef = useRef<number | null>(null);

  const navigationUnlockTimerRef = useRef<number | null>(null);
  const navigatingRef = useRef(false);

  const sessionKey = isOldTranscriptMode ? oldSessionKey : bootstrapSessionKey || reduxSessionKey;

  const isPublic = hearingRoomMode === 'public';
  const themeClassName = getRealTimeThemeClassName(theme);
  const locationCaseId = location?.state?.caseId;
  const resolvedCaseId = useMemo(() => {
    const value = routeCaseId || locationCaseId || caseId;
    return value ? String(value) : '';
  }, [routeCaseId, locationCaseId, caseId]);

  const brandText = useMemo(() => {
    const caseName = currentCase?.name || '';
    const claimNumber = currentCase?.claimNumber ? ` ${currentCase.claimNumber}` : '';
    const modeText = T.translate(isPublic ? 'case.publicHearingRoom' : 'case.privateHearingRoom');
    const title = T.translate('case.realTimeTranscript', {
      defaultValue: 'Real-time transcript',
    });

    return [caseName ? `${caseName}${claimNumber}` : '', modeText, title]
      .filter(Boolean)
      .join(' - ');
  }, [currentCase?.name, currentCase?.claimNumber, isPublic]);

  useEffect(() => {
    if (isThemeSaving) return;
    setTheme(persistedTheme);
  }, [isThemeSaving, persistedTheme]);

  useEffect(() => {
    if (isTimestampsSaving) return;
    setShowTimestamps(persistedShowTimestamps);
  }, [isTimestampsSaving, persistedShowTimestamps]);

  useEffect(() => {
    if (isVisibilitySaving) return;
    setVisibilitySettings(persistedVisibilitySettings);
  }, [isVisibilitySaving, persistedVisibilitySettings]);

  // ReindexRange: re-fetch full session transcript when the backend requests a resync
  useEffect(() => {
    if (!reindexRangePending || !sessionKey || !resolvedCaseId) return;
    clearReindexRange();
    fetchSessionTranscripts({ caseId: resolvedCaseId, sessionKey })
      .then((data: unknown) => {
        if (Array.isArray(data)) dispatch(receiveRealTimeTranscriptMessage(data));
      })
      .catch((err: unknown) => Logger.ERROR('ReindexRange refetch failed', err));
  }, [reindexRangePending, sessionKey, resolvedCaseId, dispatch, clearReindexRange]);

  const handleThemeChange = useCallback(
    async (nextTheme: RealTimeTheme) => {
      if (isThemeSaving || nextTheme === theme) return;

      const previousTheme = theme;
      setTheme(nextTheme);
      setIsThemeSaving(true);
      try {
        await updateUserSettings({
          userSettings: {
            ...(userSettings || {}),
            realTimeTheme: nextTheme,
          },
        });
      } catch (error) {
        setTheme(previousTheme);
        Logger.ERROR('Failed to update real-time theme setting', error);
      } finally {
        setIsThemeSaving(false);
      }
    },
    [isThemeSaving, theme, updateUserSettings, userSettings],
  );

  const handleTimestampsChange = useCallback(
    async (nextValue: boolean) => {
      if (isTimestampsSaving || nextValue === showTimestamps) return;

      const previousValue = showTimestamps;
      setShowTimestamps(nextValue);
      setIsTimestampsSaving(true);
      try {
        await updateUserSettings({
          userSettings: {
            ...(userSettings || {}),
            realTimeShowTimestamps: nextValue,
          },
        });
      } catch (error) {
        setShowTimestamps(previousValue);
        Logger.ERROR('Failed to update real-time timestamps setting', error);
      } finally {
        setIsTimestampsSaving(false);
      }
    },
    [isTimestampsSaving, showTimestamps, updateUserSettings, userSettings],
  );

  const handleVisibilitySettingChange = useCallback(
    async (kind: keyof VisibilitySettings, value: Visibility) => {
      if (isVisibilitySaving || visibilitySettings[kind] === value) return;

      const keyMap: Record<keyof VisibilitySettings, string> = {
        highlight: 'realTimeHighlightVisibility',
        note: 'realTimeNoteVisibility',
        tag: 'realTimeTagVisibility',
      };
      const previous = visibilitySettings;
      setVisibilitySettings((prev) => ({ ...prev, [kind]: value }));
      setIsVisibilitySaving(true);
      try {
        await updateUserSettings({
          userSettings: {
            ...(userSettings || {}),
            [keyMap[kind]]: value,
          },
        });
      } catch (error) {
        setVisibilitySettings(previous);
        Logger.ERROR('Failed to update real-time visibility setting', error);
      } finally {
        setIsVisibilitySaving(false);
      }
    },
    [isVisibilitySaving, visibilitySettings, updateUserSettings, userSettings],
  );

  const bootstrapLines = useMemo(
    () => normalizeBootstrapLines(bootstrapTranscripts),
    [bootstrapTranscripts],
  );
  const oldBootstrapLines = useMemo(
    () => normalizeBootstrapLines(oldTranscriptLines),
    [oldTranscriptLines],
  );

  const displayLines = useMemo((): RealTimeTranscriptLine[] => {
    if (isOldTranscriptMode) return oldBootstrapLines;
    // Gate: hold off showing WebSocket lines until bootstrap has rendered.
    // Messages received before bootstrap completes accumulate in Redux and merge
    // instantly (no animation) once the gate opens — correct behaviour since they
    // represent content that arrived before the UI was ready.
    if (!bootstrapReady) return bootstrapLines;
    if (!lines.length) return bootstrapLines;

    const byId = new Map<string, RealTimeTranscriptLine>();
    const byLineNumber = new Map<number, string>(); // lineNumber → canonical id in byId
    const order: string[] = [];
    // Track the id of the open line from bootstrap (if any). The third fallback
    // must only ever match THIS specific line — never a WS-derived open line —
    // otherwise every new partial WS line gets merged into the previous open line
    // instead of being appended as a new entry.
    let bootstrapOpenLineId: string | null = null;

    bootstrapLines.forEach((line) => {
      if (!byId.has(line.id)) {
        order.push(line.id);
        if (line.lineNumber != null) byLineNumber.set(line.lineNumber, line.id);
      }
      byId.set(line.id, line);
      if (line.isFinal === false) bootstrapOpenLineId = line.id;
    });

    lines.forEach((line: RealTimeTranscriptLine) => {
      // Primary match by id; fallback to lineNumber when the server uses different
      // identifier fields across bootstrap (lineId/GUID) and WebSocket (lineNumber).
      let existing = byId.get(line.id);
      let canonicalId = line.id;
      if (!existing && line.lineNumber != null) {
        const bootstrapId = byLineNumber.get(line.lineNumber);
        if (bootstrapId != null) {
          existing = byId.get(bootstrapId);
          canonicalId = bootstrapId;
        }
      }

      // Third fallback: an unmatched WS update for the current open line won't
      // match the bootstrap openLine by id or lineNumber because open lines have
      // neither assigned yet. Route it to the bootstrap open line only (never a
      // WS-derived open line) so new lines are appended rather than merged.
      // Guard: if the incoming WS line carries identity (lineNumber or lineId)
      // that differs from the bootstrap open line, it is a distinct new line —
      // do NOT route it to the open line or it will overwrite line 6 with
      // lines 7, 8, 9, … in a loop.
      if (!existing && bootstrapOpenLineId != null) {
        const candidate = byId.get(bootstrapOpenLineId);
        const wsLineHasDistinctIdentity =
          (line.lineNumber != null && line.lineNumber !== candidate?.lineNumber) ||
          (line.lineId != null && line.lineId !== candidate?.lineId);
        if (
          !wsLineHasDistinctIdentity &&
          candidate?.isFinal === false &&
          (line.isFinal === false || line.type === 'linefinalized')
        ) {
          existing = candidate;
          canonicalId = bootstrapOpenLineId;
        }
      }

      if (!existing) {
        byId.set(line.id, line);
        order.push(line.id);
        if (line.lineNumber != null) byLineNumber.set(line.lineNumber, line.id);
        return;
      }

      const incomingVersion = line.version;
      const existingVersion = existing.version;
      const shouldOverride =
        incomingVersion != null && existingVersion != null
          ? incomingVersion >= existingVersion
          : !(line.isFinal === false && existing.isFinal === true);

      if (!shouldOverride) return;

      const hasIncomingText = line.text != null;

      byId.set(canonicalId, {
        ...existing,
        ...line,
        id: canonicalId,
        text: hasIncomingText ? line.text : existing.text,
        lineId: line.lineId ?? existing.lineId,
        lineNumber: line.lineNumber ?? existing.lineNumber,
        pageNumber: line.pageNumber ?? existing.pageNumber,
        pageLineNumber: line.pageLineNumber ?? existing.pageLineNumber,
        version: line.version ?? existing.version,
        observedAtUtc: line.observedAtUtc ?? existing.observedAtUtc ?? null,
        finalizedAtUtc: line.finalizedAtUtc ?? existing.finalizedAtUtc ?? null,
        // Only linefinalized explicitly closes an open line. All correction events
        // (linecorrected, multicorrected) preserve the existing open/closed state
        // so the cursor never moves away from the last line.
        isFinal:
          existing.isFinal === false && line.type !== 'linefinalized'
            ? false
            : (line.isFinal ?? existing.isFinal),
      });
    });

    const result = order.map((id) => byId.get(id)).filter(Boolean) as RealTimeTranscriptLine[];

    // Enforce the invariant that at most one line is open at a time. When
    // multiple linecorrected events arrive in the same batch for different lines
    // (no intervening linefinalized), both end up isFinal:false. Keep only the
    // last one open; treat all earlier ones as finalized so they don't animate
    // simultaneously and don't show the partial highlight.
    let lastOpenIdx = -1;
    for (let i = result.length - 1; i >= 0; i -= 1) {
      if (result[i].isFinal === false) {
        lastOpenIdx = i;
        break;
      }
    }
    if (lastOpenIdx > 0) {
      for (let i = 0; i < lastOpenIdx; i += 1) {
        if (result[i].isFinal === false) {
          result[i] = { ...result[i], isFinal: true };
        }
      }
    }

    return result;
  }, [isOldTranscriptMode, oldBootstrapLines, bootstrapReady, bootstrapLines, lines]);

  const lineNumbersByLineId = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of displayLines) {
      const num = line.pageLineNumber ?? line.lineNumber;
      if (num != null) map.set(line.id, num);
    }
    return map;
  }, [displayLines]);

  const pageNumbersByLineId = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of displayLines) {
      if (line.pageNumber != null) map.set(line.id, line.pageNumber);
    }
    return map;
  }, [displayLines]);

  const openLineIds = useMemo(() => {
    const set = new Set<string>();
    for (const line of displayLines) {
      if (line.isFinal === false) set.add(line.id);
    }
    return set;
  }, [displayLines]);

  const {
    pendingSelection,
    activeNoteId,
    isEditingExisting,
    isHighlightOnly,
    isTagMode,
    isRemoteNote,
    availableTags,
    highlightsByLine,
    captureSelection,
    commitHighlight,
    commitTag,
    updateNote,
    updateTags,
    createTag,
    removeHighlight,
    openNoteForHighlight,
    dismissPopup,
    highlights,
    toggleVisibility,
    submitComment,
  } = useTextHighlights(scrollRef, {
    caseId: resolvedCaseId,
    sessionKey,
    userId,
    identityId,
    lineNumbersByLineId,
    pageNumbersByLineId,
    openLineIds,
    visibilitySettings,
    noteNotifications,
    userName,
  });

  const filteredHighlightsByLine = useMemo(() => {
    if (!hideMyAnnotations && !hideOthersAnnotations) return highlightsByLine;
    const filtered = new Map<string, TranscriptHighlight[]>();
    for (const [lineId, hls] of highlightsByLine) {
      const kept = hls.filter((hl) => {
        const isMine = hl.createdByUserId ? hl.createdByUserId === identityId : true;
        if (hideMyAnnotations && isMine) return false;
        if (hideOthersAnnotations && !isMine) return false;
        return true;
      });
      if (kept.length > 0) filtered.set(lineId, kept);
    }
    return filtered;
  }, [highlightsByLine, hideMyAnnotations, hideOthersAnnotations, identityId]);

  const filteredHighlights = useMemo(() => {
    if (!hideMyAnnotations && !hideOthersAnnotations) return highlights;
    return highlights.filter((hl) => {
      const isMine = hl.createdByUserId ? hl.createdByUserId === identityId : true;
      if (hideMyAnnotations && isMine) return false;
      if (hideOthersAnnotations && !isMine) return false;
      return true;
    });
  }, [highlights, hideMyAnnotations, hideOthersAnnotations, identityId]);

  const pendingSelectionRef = useRef(pendingSelection);
  pendingSelectionRef.current = pendingSelection;
  const activeNoteIdRef = useRef(activeNoteId);
  activeNoteIdRef.current = activeNoteId;

  const {
    query: searchQuery,
    isSearchActive,
    activeMatchIndex,
    totalMatches,
    matchedLineIds,
    activeLineId,
    hasPendingNewLines,
    setQuery: setSearchQuery,
    goNext: searchGoNext,
    goPrev: searchGoPrev,
    clearSearch,
    refreshSnapshot,
  } = useTranscriptSearch(displayLines, scrollRef);

  const { navigateToDocRef } = useDocFinderNavigation(resolvedCaseId);

  const isSearchActiveRef = useRef(isSearchActive);
  isSearchActiveRef.current = isSearchActive;

  const handleSearchQueryChange = useCallback(
    (q: string) => {
      if (q.trim().length > 0 && autoScroll) {
        setAutoScroll(false);
      }
      setSearchQuery(q);
    },
    [setSearchQuery, autoScroll],
  );

  const handleClearSearch = useCallback(() => {
    clearSearch();
    setAutoScroll(true);
  }, [clearSearch]);

  const handleSearchGoNext = useCallback(() => {
    setAutoScroll(false);
    searchGoNext();
  }, [searchGoNext]);

  const handleSearchGoPrev = useCallback(() => {
    setAutoScroll(false);
    searchGoPrev();
  }, [searchGoPrev]);

  const activeNoteText = useMemo(() => {
    if (!activeNoteId) return '';
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.note ?? '';
  }, [activeNoteId, highlights]);

  const handleMouseDown = useCallback(() => {
    isSelectingRef.current = true;
  }, []);

  const handleMouseUp = useCallback(() => {
    if (isOldTranscriptMode) {
      isSelectingRef.current = false;
      return;
    }
    requestAnimationFrame(() => {
      const captured = captureSelection();
      if (!captured) {
        isSelectingRef.current = false;
      }
      // If captured, keep isSelectingRef true so the drain animation stays
      // paused while the popup is visible. It gets cleared when
      // pendingSelection is dismissed/committed (see useEffect below).
    });
  }, [captureSelection, isOldTranscriptMode]);

  // Resume drain animation once the selection popup is dismissed or committed
  useEffect(() => {
    if (!pendingSelection) {
      isSelectingRef.current = false;
    }
  }, [pendingSelection]);

  const handleHighlightClick = useCallback(() => {
    commitHighlight(false);
  }, [commitHighlight]);

  const handleNoteClick = useCallback(() => {
    commitHighlight(true);
  }, [commitHighlight]);

  const handleTagClick = useCallback(() => {
    commitTag();
  }, [commitTag]);

  const activeTags = useMemo(() => {
    if (!activeNoteId) return [];
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.tags ?? [];
  }, [activeNoteId, highlights]);

  const activeVisibility = useMemo(() => {
    if (!activeNoteId) return 'private' as const;
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.visibility ?? ('private' as const);
  }, [activeNoteId, highlights]);

  const activeIsLocalOnly = useMemo(() => {
    if (!activeNoteId) return false;
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return !!hl?.isLocalOnly;
  }, [activeNoteId, highlights]);

  const activeCreatedByName = useMemo(() => {
    if (!activeNoteId) return undefined;
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.createdByName;
  }, [activeNoteId, highlights]);

  const activeCreatedAt = useMemo(() => {
    if (!activeNoteId) return undefined;
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.createdAt;
  }, [activeNoteId, highlights]);

  const activeComments = useMemo(() => {
    if (!activeNoteId) return [];
    const hl = highlights.find((h) => h.highlightId === activeNoteId);
    return hl?.comments ?? [];
  }, [activeNoteId, highlights]);

  const handleSubmitComment = useCallback(
    async (commentText: string): Promise<boolean> => {
      if (!activeNoteId) return false;
      return submitComment(activeNoteId, commentText);
    },
    [activeNoteId, submitComment],
  );

  const handleVisibilityToggle = useCallback(() => {
    if (activeNoteId) toggleVisibility(activeNoteId);
  }, [activeNoteId, toggleVisibility]);

  const handleLogClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const mark = target.closest('[data-highlight-id]') as HTMLElement | null;

      // Dismiss navigation highlight when clicking outside the navigated highlight
      if (navigatedHighlightId) {
        const clickedHighlightId = mark?.getAttribute('data-highlight-id');
        if (clickedHighlightId !== navigatedHighlightId) {
          setNavigatedHighlightId(null);
        }
      }

      if (!mark) return;

      // Only open note on a genuine click, not the end of a drag-selection
      const selection = document.getSelection();
      if (selection && !selection.isCollapsed) return;

      const highlightId = mark.getAttribute('data-highlight-id');
      if (!highlightId) return;

      if (isOldTranscriptMode) {
        // In old transcript mode: view existing notes only — skip plain highlights with no text
        const hl = highlights.find((h) => h.highlightId === highlightId);
        if (hl?.note) openNoteForHighlight(highlightId);
        return;
      }

      openNoteForHighlight(highlightId);
    },
    [openNoteForHighlight, isOldTranscriptMode, highlights, navigatedHighlightId],
  );

  const isSelectionInsideLog = useCallback((selection: Selection | null) => {
    const container = scrollRef.current;
    if (!container || !selection || selection.isCollapsed) return false;

    const isNodeInside = (node: Node | null) => {
      if (!node) return false;
      const element = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
      return !!element && container.contains(element);
    };

    return isNodeInside(selection.anchorNode) || isNodeInside(selection.focusNode);
  }, []);

  const displayLinesWithPages = useMemo(
    (): RealTimeTranscriptLine[] => displayLines,
    [displayLines],
  );

  const textDisplayLines = useMemo(
    () =>
      displayLinesWithPages.filter(
        (line) => (typeof line.text === 'string' && line.text.length > 0) || hasLineIdentity(line),
      ),
    [displayLinesWithPages],
  );

  const renderItems = useMemo(() => {
    const items: Array<
      | { kind: 'divider'; key: string; pageNumber?: number }
      | { kind: 'line'; key: string; line: RealTimeTranscriptLine }
    > = [];
    let currentPage: number | undefined;

    displayLinesWithPages.forEach((entry, index) => {
      if (!(typeof entry.text === 'string' && entry.text.length > 0) && !hasLineIdentity(entry)) {
        return;
      }

      if (entry.pageNumber !== currentPage) {
        currentPage = entry.pageNumber;
        items.push({
          kind: 'divider',
          key: `page-auto-${entry.id || index}`,
          pageNumber: currentPage,
        });
      }

      items.push({
        kind: 'line',
        key: `line-${entry.id || index}`,
        line: entry,
      });
    });

    return items;
  }, [displayLinesWithPages]);

  const latestAnimatingLineId = useMemo(() => {
    for (let index = renderItems.length - 1; index >= 0; index -= 1) {
      const item = renderItems[index];
      if (item.kind === 'line' && item.line.isFinal === false) {
        return item.line.id;
      }
    }
    return null;
  }, [renderItems]);

  const triggerLogScrollbar = useCallback(() => {
    setShowLogScrollbar(true);
    if (logScrollbarTimerRef.current) {
      window.clearTimeout(logScrollbarTimerRef.current);
    }
    logScrollbarTimerRef.current = window.setTimeout(() => {
      setShowLogScrollbar(false);
    }, 1000);
  }, []);

  const navigateToTranscriptLines = useCallback(
    (lineIds: string[], highlightId?: string) => {
      const container = scrollRef.current;
      if (!container || lineIds.length === 0) return;

      const targetLineId = lineIds.find(
        (lineId) => !!container.querySelector<HTMLElement>(`[data-line-id="${lineId}"]`),
      );

      if (!targetLineId) return;

      const targetElement = container.querySelector<HTMLElement>(
        `[data-line-id="${targetLineId}"]`,
      );
      if (!targetElement) return;

      setAutoScroll(false);
      setHasUnread(false);
      triggerLogScrollbar();

      navigatingRef.current = true;
      container.classList.add('real-time__log--navigating');

      if (navigationUnlockTimerRef.current) {
        window.clearTimeout(navigationUnlockTimerRef.current);
      }

      requestAnimationFrame(() => {
        targetElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });

      navigationUnlockTimerRef.current = window.setTimeout(() => {
        container.classList.remove('real-time__log--navigating');
        navigatingRef.current = false;
      }, 800);

      if (!highlightId) return;

      setNavigatedHighlightId(highlightId);
    },
    [triggerLogScrollbar],
  );

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    triggerLogScrollbar();
    requestAnimationFrame(() => {
      container.scrollTop = container.scrollHeight;
    });
  }, [triggerLogScrollbar]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    triggerLogScrollbar();

    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceToBottom < AUTO_SCROLL_THRESHOLD;

    setIsScrolledAway(!isNearBottom);

    // Don't re-enable auto-scroll while a Go-to-page navigation is in flight
    if (navigatingRef.current) return;

    // Don't re-enable auto-scroll while search is active, but still allow disabling it
    if (isSearchActiveRef.current) {
      if (!isNearBottom) setAutoScroll(false);
      return;
    }

    // Don't re-enable auto-scroll while selection popup or note panel is open
    if (pendingSelectionRef.current || activeNoteIdRef.current) return;

    if (isNearBottom) {
      setAutoScroll(true);
      setHasUnread(false);
    } else {
      setAutoScroll(false);
    }
  }, [triggerLogScrollbar]);

  const handleJumpToLive = useCallback(() => {
    setAutoScroll(true);
    setHasUnread(false);
    setIsScrolledAway(false);
    scrollToBottom();
  }, [scrollToBottom]);

  const handleExport = useCallback(() => {
    exportTranscriptPdf(displayLinesWithPages, highlights, brandText, { showTimestamps });
  }, [displayLinesWithPages, highlights, brandText, showTimestamps]);

  const handleExportTxt = useCallback(() => {
    exportTranscriptTxt(displayLinesWithPages, highlights, brandText, { showTimestamps });
  }, [displayLinesWithPages, highlights, brandText, showTimestamps]);

  const handleOldTranscriptsButtonClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!resolvedCaseId || isLoadingOldTranscript) return;
      setOldTranscriptsDropdownAnchor(event.currentTarget);
      if (oldTranscriptSessions.length > 0) return;
      setIsLoadingOldTranscript(true);
      try {
        const response = await fetchEndedSessions({ caseId: resolvedCaseId });
        if (isErrorResponse(response) && response.error) {
          Logger.ERROR('Failed to fetch sessions', response.error);
          return;
        }
        const sessions: OldTranscriptSession[] = Array.isArray(response)
          ? response
          : isPlainObject(response)
            ? (response.data ?? response.results ?? response.items ?? [])
            : [];
        setOldTranscriptSessions(sessions);
      } catch (error) {
        Logger.ERROR('Failed to fetch old transcript sessions', error);
      } finally {
        setIsLoadingOldTranscript(false);
      }
    },
    [resolvedCaseId, isLoadingOldTranscript, oldTranscriptSessions.length],
  );

  const handleSelectSession = useCallback(
    async (session: OldTranscriptSession) => {
      if (!resolvedCaseId) return;

      setIsOldTranscriptMode(true);
      setAutoScroll(false);
      setOldTranscriptsDropdownAnchor(null);
      setIsLoadingSessionLines(true);
      setSelectedOldSession(session);
      setSessionsMenuAnchor(null);
      try {
        const response = await fetchSessionTranscripts({
          caseId: resolvedCaseId,
          sessionKey: session.sessionKey,
        });

        const sessionLines =
          isPlainObject(response) && Array.isArray(response.lines)
            ? response.lines
            : extractBootstrapLines(response);

        setOldTranscriptLines(sessionLines);
        setOldSessionKey(session.sessionKey);
      } catch (error) {
        Logger.ERROR('Failed to fetch session transcripts', error);
      } finally {
        setIsLoadingSessionLines(false);
      }
    },
    [resolvedCaseId],
  );

  const handleLoadLiveTranscripts = useCallback(() => {
    setIsOldTranscriptMode(false);
    setOldTranscriptLines([]);
    setOldSessionKey(null);
    setOldTranscriptSessions([]);
    setSelectedOldSession(null);
    setSessionsMenuAnchor(null);
    setOldTranscriptsDropdownAnchor(null);

    clear();
    setBootstrapTranscripts([]);
    setBootstrapReady(false);
    setBootstrapSessionKey(null);
    bootstrapCompletedRef.current = null;
    bootstrapInFlightRef.current = null;
    setBootstrapTrigger((prev) => prev + 1);
    setAutoScroll(true);
  }, [clear]);

  const handleGoToPage = useCallback(() => {
    const page = Number(goToPageInput);
    if (!page || Number.isNaN(page)) return;

    const container = scrollRef.current;
    if (!container) return;

    const divider = container.querySelector<HTMLElement>(`[data-page="${page}"]`);
    if (!divider) return;

    setAutoScroll(false);
    setHasUnread(false);
    triggerLogScrollbar();
    navigatingRef.current = true;

    // Temporarily disable content-visibility so the browser can compute
    // accurate positions for elements above the current scroll position.
    container.classList.add('real-time__log--navigating');

    requestAnimationFrame(() => {
      divider.scrollIntoView({ block: 'center', behavior: 'smooth' });

      // Restore content-visibility and navigating lock after scroll settles
      setTimeout(() => {
        container.classList.remove('real-time__log--navigating');
        navigatingRef.current = false;
      }, 800);
    });
  }, [goToPageInput, triggerLogScrollbar]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleSelectionChange = () => {
      if (!autoScroll) return;
      const selection = document.getSelection();
      if (!isSelectionInsideLog(selection)) return;
      setAutoScroll(false);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [autoScroll, isSelectionInsideLog]);

  useEffect(() => {
    return () => {
      if (logScrollbarTimerRef.current) {
        window.clearTimeout(logScrollbarTimerRef.current);
      }

      if (navigationUnlockTimerRef.current) {
        window.clearTimeout(navigationUnlockTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    clear();
    setBootstrapTranscripts([]);
    setBootstrapReady(false);
    setBootstrapSessionKey(null);
    return () => clear();
  }, [resolvedCaseId, clear]);

  const proceedWithRestart = useCallback(() => {
    clear();
    setBootstrapTranscripts([]);
    setBootstrapReady(false);
    setBootstrapSessionKey(null);
    bootstrapCompletedRef.current = null;
    bootstrapInFlightRef.current = null;
    setBootstrapTrigger((prev) => prev + 1);
    setShowRestartDialog(false);
  }, [clear]);

  useEffect(() => {
    if (!sessionRestartedAt || isOldTranscriptMode) return;

    // If session_restarted with same sessionKey, auto-proceed without dialog
    if (
      sessionNotificationType === 'session_restarted' &&
      restartSessionKey &&
      sessionKey &&
      restartSessionKey === sessionKey
    ) {
      proceedWithRestart();
      return;
    }

    // If no existing transcripts, skip the dialog and auto-proceed
    if (!lines.length && !bootstrapTranscripts.length) {
      proceedWithRestart();
      return;
    }

    setShowRestartDialog(true);
  }, [
    sessionRestartedAt,
    isOldTranscriptMode,
    sessionNotificationType,
    restartSessionKey,
    sessionKey,
    proceedWithRestart,
    lines.length,
    bootstrapTranscripts.length,
  ]);

  const handleRestartExport = useCallback(() => {
    handleExport();
    proceedWithRestart();
  }, [handleExport, proceedWithRestart]);

  const handleRestartContinue = useCallback(() => {
    proceedWithRestart();
  }, [proceedWithRestart]);

  useEffect(() => {
    if (!resolvedCaseId || !hubConnectionId || !accessToken) return;

    const bootstrapKey = `${resolvedCaseId}:${hubConnectionId}`;
    if (bootstrapCompletedRef.current === bootstrapKey) return;
    if (bootstrapInFlightRef.current === bootstrapKey) return;

    bootstrapInFlightRef.current = bootstrapKey;

    clear();

    const controller = new AbortController();

    const runBootstrap = async () => {
      try {
        const response = await bootstrapRealTime({
          caseId: resolvedCaseId,
          connectionId: hubConnectionId,
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        if (isErrorResponse(response) && response.error) {
          Logger.ERROR('RealTime bootstrap failed', response.error);
          return;
        }

        // Extract sessionKey from bootstrap response
        if (isPlainObject(response)) {
          const sk = response.sessionKey ?? null;
          if (sk) setBootstrapSessionKey(String(sk));
        }

        const nextBootstrapLines = extractBootstrapLines(response);
        setBootstrapTranscripts(nextBootstrapLines);

        bootstrapCompletedRef.current = bootstrapKey;
      } catch (error) {
        if (controller.signal.aborted) return;
        Logger.ERROR('RealTime bootstrap failed', error);
      } finally {
        if (!controller.signal.aborted) {
          bootstrapInFlightRef.current = null;
          // Always open the gate regardless of success/failure/empty response
          // so WebSocket lines are never permanently blocked from rendering.
          setBootstrapReady(true);
        }
      }
    };

    runBootstrap();
    return () => {
      controller.abort();
      bootstrapInFlightRef.current = null;
    };
  }, [resolvedCaseId, hubConnectionId, accessToken, bootstrapTrigger, clear]);

  useEffect(() => {
    if (isOldTranscriptMode) return;

    const nextCount = textDisplayLines.length;
    const hasNewLines = nextCount > previousCountRef.current;
    const hasNewEvent = lastEventAt && lastEventAt !== previousEventRef.current;

    previousCountRef.current = nextCount;
    previousEventRef.current = lastEventAt || null;

    const hasActivePopup = !!pendingSelectionRef.current || !!activeNoteIdRef.current;

    // Keep following live updates whenever auto-scroll is explicitly on.
    // Search only blocks implicit re-enabling from manual scroll.
    if (autoScroll && !hasActivePopup) {
      scrollToBottom();
      setHasUnread(false);
    } else if (hasNewLines || hasNewEvent) {
      // Only show "Jump to latest" if the user is actually scrolled away from the bottom
      const container = scrollRef.current;
      if (container) {
        const distanceToBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom > AUTO_SCROLL_THRESHOLD) {
          setHasUnread(true);
        }
      }
    }
  }, [
    isOldTranscriptMode,
    autoScroll,
    lastEventAt,
    textDisplayLines.length,
    scrollToBottom,
  ]);

  const sessionItems = useMemo(() => {
    const sorted = [...oldTranscriptSessions].sort(
      (a, b) => new Date(a.startedAtUtc).getTime() - new Date(b.startedAtUtc).getTime(),
    );

    // Group sessions by calendar date to assign Day numbers
    const dateGroups = new Map<string, OldTranscriptSession[]>();
    sorted.forEach((session) => {
      const dateKey = format(new Date(session.startedAtUtc), 'yyyy-MM-dd');
      const group = dateGroups.get(dateKey) ?? [];
      group.push(session);
      dateGroups.set(dateKey, group);
    });

    const labelMap = new Map<string, string>();
    let dayNumber = 0;
    dateGroups.forEach((group) => {
      dayNumber += 1;
      if (group.length === 1) {
        labelMap.set(group[0].id, `Day ${dayNumber}`);
      } else {
        group.forEach((session, index) => {
          labelMap.set(session.id, `Day ${dayNumber} - ${String.fromCharCode(65 + index)}`);
        });
      }
    });

    return sorted.map((session) => ({ session, dayLabel: labelMap.get(session.id) ?? '' }));
  }, [oldTranscriptSessions]);

  const isLoading = fetchCasesPending || fetchCaseDetailsPending;
  const handleCommentNoteClick = useCallback(
    (note: TranscriptHighlight) => {
      navigateToTranscriptLines(getNoteLineIds(note), note.highlightId);
    },
    [navigateToTranscriptLines],
  );

  if (isLoading) {
    return (
      <div className={classnames(themeClassName, 'case-theatre real-time__loading-screen')}>
        <div className="real-time__loading">
          {T.translate('generic.loading', { defaultValue: 'Loading...' })}
        </div>
      </div>
    );
  }

  const lastUpdateLabel = lastEventAt ? formatTimestamp(lastEventAt) : null;
  const isLive = lastEventAt ? Date.now() - lastEventAt < LIVE_WINDOW_MS : false;

  return (
    <div className={classnames(themeClassName, 'case-theatre')}>
      <NavBar
        showLogo
        theme={themeClassName}
        brandText={brandText}
        additionalButton={[<FullScreenButton key="fullscreen" />].filter(Boolean)}
      />
      <div className="real-time">
        <div className="real-time__header">
          <div className="real-time__title">
            <div className="real-time__meta">
              {isOldTranscriptMode ? (
                <span className="real-time__status real-time__status--old">
                  {selectedOldSession
                    ? (sessionItems.find(({ session }) => session.id === selectedOldSession.id)
                        ?.dayLabel ?? 'Old Transcript')
                    : 'Old Transcript'}
                </span>
              ) : (
                <>
                  <span className={classnames('real-time__status', { 'is-live': isLive })}>
                    <span className="real-time__status-dot" />
                    {isLive
                      ? T.translate('case.realTimeLive', { defaultValue: 'Live' })
                      : T.translate('case.realTimeWaiting', {
                          defaultValue: 'Waiting for live feed',
                        })}
                  </span>
                  {lastUpdateLabel && (
                    <span className="real-time__timestamp">
                      {T.translate('case.realTimeLastUpdate', {
                        defaultValue: `Last update ${lastUpdateLabel}`,
                      })}
                    </span>
                  )}
                </>
              )}
              <span className="real-time__count">
                {T.translate('case.realTimeLineCount', {
                  defaultValue: `${textDisplayLines.length} lines`,
                })}
              </span>
            </div>
          </div>
          <div className="real-time__header-center">
            <div className="real-time__go-to-page">
              <input
                type="number"
                className="real-time__go-to-page-input"
                placeholder={
                  T.translate('case.realTimeGoToPagePlaceholder', {
                    defaultValue: 'Page #',
                  }) as string
                }
                value={goToPageInput}
                onFocus={() => setAutoScroll(false)}
                onChange={(e) => setGoToPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleGoToPage();
                  }
                }}
                min={1}
              />
              <button
                type="button"
                className="real-time__go-to-page-btn"
                onClick={handleGoToPage}
                disabled={!goToPageInput}
              >
                {T.translate('case.realTimeGoTo', { defaultValue: 'Go to' })}
              </button>
            </div>
            <div className="real-time__search">
              <div className="real-time__search-input-wrap">
                <SearchIcon className="real-time__search-icon" fontSize="small" />
                <input
                  type="text"
                  className="real-time__search-input"
                  placeholder={
                    T.translate('case.realTimeSearchPlaceholder', {
                      defaultValue: 'Search',
                    }) as string
                  }
                  value={searchQuery}
                  onChange={(e) => handleSearchQueryChange(e.target.value)}
                  onFocus={() => setAutoScroll(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (e.shiftKey) {
                        handleSearchGoPrev();
                      } else {
                        handleSearchGoNext();
                      }
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleClearSearch();
                    }
                  }}
                  aria-label={
                    T.translate('case.realTimeSearchLabel', {
                      defaultValue: 'Search transcript',
                    }) as string
                  }
                />
                {isSearchActive && (
                  <button
                    type="button"
                    className="real-time__search-clear"
                    onClick={handleClearSearch}
                    aria-label={
                      T.translate('case.realTimeSearchClear', {
                        defaultValue: 'Clear search',
                      }) as string
                    }
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
              {isSearchActive && (
                <div className="real-time__search-results">
                  <span className="real-time__search-count" aria-live="polite">
                    {totalMatches > 0 ? `${activeMatchIndex + 1} of ${totalMatches}` : '0 of 0'}
                  </span>
                  <button
                    type="button"
                    className="real-time__search-nav"
                    onClick={handleSearchGoPrev}
                    disabled={totalMatches === 0}
                    aria-label={
                      T.translate('case.realTimeSearchPrev', {
                        defaultValue: 'Previous match',
                      }) as string
                    }
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className="real-time__search-nav"
                    onClick={handleSearchGoNext}
                    disabled={totalMatches === 0}
                    aria-label={
                      T.translate('case.realTimeSearchNext', {
                        defaultValue: 'Next match',
                      }) as string
                    }
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </button>
                </div>
              )}
              {hasPendingNewLines && (
                <button
                  type="button"
                  className="real-time__search-refresh"
                  onClick={refreshSnapshot}
                >
                  {T.translate('case.realTimeSearchRefresh', {
                    defaultValue: 'New lines available \u2013 Update search',
                  })}
                </button>
              )}
            </div>
          </div>
          <div className="real-time__actions">
            {isOldTranscriptMode ? (
              <>
                <button
                  type="button"
                  className="real-time__load-live-btn"
                  onClick={handleLoadLiveTranscripts}
                >
                  Load Live Transcripts
                </button>
              </>
            ) : (
              <FormControlLabel
                className="real-time__toggle"
                control={
                  <Switch
                    size="small"
                    checked={autoScroll}
                    onChange={(event) => setAutoScroll(event.target.checked)}
                  />
                }
                label={T.translate('case.realTimeAutoScroll', { defaultValue: 'Auto-scroll' })}
              />
            )}
            {/* Old Transcripts button */}
            <div className="real-time__old-transcripts">
              <Tooltip
                title="Old Transcripts"
                componentsProps={{
                  tooltip: { className: theme === 'dark' ? 'real-time-tooltip-dark' : '' },
                  arrow: { className: theme === 'dark' ? 'real-time-tooltip-dark-arrow' : '' },
                }}
              >
                <button
                  type="button"
                  className="real-time__old-transcripts-btn"
                  onClick={handleOldTranscriptsButtonClick}
                  disabled={isLoadingOldTranscript}
                  aria-label="Old Transcripts"
                  aria-haspopup="menu"
                  aria-expanded={oldTranscriptsDropdownAnchor ? 'true' : undefined}
                >
                  <HistoryIcon fontSize="small" />
                </button>
              </Tooltip>
              <Menu
                anchorEl={oldTranscriptsDropdownAnchor}
                open={Boolean(oldTranscriptsDropdownAnchor)}
                onClose={() => setOldTranscriptsDropdownAnchor(null)}
                classes={{ paper: 'real-time__sessions-menu' }}
                disablePortal
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {isLoadingOldTranscript ? (
                  <div className="real-time__old-transcripts-loading">Loading sessions...</div>
                ) : oldTranscriptSessions.length === 0 ? (
                  <div className="real-time__old-transcripts-empty">No sessions found.</div>
                ) : (
                  sessionItems.map(({ session, dayLabel }) => (
                    <MenuItem
                      key={session.id}
                      className={classnames('real-time__sessions-menu-item', {
                        'is-active': selectedOldSession?.id === session.id,
                      })}
                      onClick={() => handleSelectSession(session)}
                    >
                      <span className="real-time__sessions-menu-day">
                        {dayLabel}
                        {session.pageCount != null && (
                          <span className="real-time__sessions-menu-pages">
                            {` (${session.pageCount} pages)`}
                          </span>
                        )}
                      </span>
                      <span className="real-time__sessions-menu-time">
                        {format(new Date(session.startedAtUtc), 'dd/MM/yyyy HH:mm')}
                        {' \u2013 '}
                        {format(new Date(session.endedAtUtc), 'HH:mm')}
                      </span>
                      <span className="real-time__sessions-menu-case">Case: {session.caseId}</span>
                    </MenuItem>
                  ))
                )}
              </Menu>
            </div>
            <ExportMenu
              disabled={textDisplayLines.length === 0}
              onExportPdf={handleExport}
              onExportTxt={handleExportTxt}
              theme={theme}
            />
            <ThemeSettingsMenu
              theme={theme}
              disabled={isThemeSaving || isTimestampsSaving || isVisibilitySaving}
              onThemeChange={handleThemeChange}
              showTimestamps={showTimestamps}
              onTimestampsChange={handleTimestampsChange}
              visibilitySettings={visibilitySettings}
              onVisibilityChange={handleVisibilitySettingChange}
            />
          </div>
        </div>
        {/* {hasChildren && <div className="real-time__aux">{children}</div>} */}
        <div className="real-time__body">
          <div className="real-time__log-wrap">
            <div
              className={classnames('real-time__log', {
                'is-scrolling': showLogScrollbar,
                'real-time__log--search-active': isSearchActive,
              })}
              ref={scrollRef}
              onScroll={handleScroll}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onClick={handleLogClick}
              role="log"
              aria-live="polite"
              aria-relevant="additions text"
            >
              {isOldTranscriptMode &&
              selectedOldSession &&
              (isLoadingSessionLines || renderItems.length === 0) ? (
                <div className="real-time__empty">
                  {isLoadingSessionLines
                    ? 'Loading transcript...'
                    : 'No lines found for this session.'}
                </div>
              ) : renderItems.length === 0 ? (
                <div className="real-time__empty">
                  {T.translate('case.realTimeEmpty', {
                    defaultValue: 'Waiting for the live transcript to start...',
                  })}
                </div>
              ) : (
                renderItems.map((item) =>
                  item.kind === 'divider' ? (
                    <div
                      key={item.key}
                      className="real-time__page-divider"
                      data-page={item.pageNumber}
                    >
                      <span className="real-time__page-divider-label">
                        PAGE {item.pageNumber ?? 'N/A'}
                      </span>
                    </div>
                  ) : (
                    <TranscriptLine
                      key={item.key}
                      line={item.line}
                      lineHighlights={filteredHighlightsByLine.get(item.line.id) || EMPTY_HIGHLIGHTS}
                      navigatedHighlightId={navigatedHighlightId}
                      shouldAnimateLive={item.line.id === latestAnimatingLineId}
                      searchHighlight={
                        isSearchActive
                          ? item.line.id === activeLineId
                            ? 'active'
                            : matchedLineIds.has(item.line.id)
                              ? 'match'
                              : null
                          : null
                      }
                      showTimestamps={showTimestamps}
                      onDocRefClick={navigateToDocRef}
                    />
                  ),
                )
              )}
            </div>
            {!autoScroll && isScrolledAway && !isOldTranscriptMode && (
              <div className="real-time__jump-wrap">
                <button className="real-time__jump" onClick={handleJumpToLive} type="button">
                  {T.translate('case.realTimeJump', { defaultValue: 'Jump to latest' })}
                </button>
              </div>
            )}
            <HighlightPopup
              pendingSelection={pendingSelection}
              activeNoteId={activeNoteId}
              isEditingExisting={isEditingExisting}
              isHighlightOnly={isHighlightOnly}
              isTagMode={isTagMode}
              isRemoteNote={isRemoteNote}
              isLocalOnly={activeIsLocalOnly}
              onHighlight={handleHighlightClick}
              onNote={handleNoteClick}
              onTag={handleTagClick}
              onDismiss={dismissPopup}
              onNoteChange={updateNote}
              onTagsChange={updateTags}
              onCreateTag={createTag}
              onRemove={removeHighlight}
              noteText={activeNoteText}
              selectedTags={activeTags}
              availableTags={availableTags}
              readOnly={isOldTranscriptMode}
              visibility={activeVisibility}
              onVisibilityToggle={handleVisibilityToggle}
              createdByName={activeCreatedByName}
              createdAt={activeCreatedAt}
              comments={activeComments}
              onSubmitComment={handleSubmitComment}
            />
          </div>
          <PresentedDocsPanel
            caseId={resolvedCaseId}
            notes={filteredHighlights}
            onCommentNoteClick={handleCommentNoteClick}
            onSubmitComment={submitComment}
            hideMyAnnotations={hideMyAnnotations}
            hideOthersAnnotations={hideOthersAnnotations}
            onHideMyAnnotationsChange={setHideMyAnnotations}
            onHideOthersAnnotationsChange={setHideOthersAnnotations}
          />
        </div>
      </div>
      <Dialog open={showRestartDialog} disableEscapeKeyDown>
        <DialogContent>
          {sessionNotificationType === 'session_resumed' ? (
            <p className="real-time__restart-dialog-text">
              Session resumed. Please reload to continue, or refresh your browser.
            </p>
          ) : (
            <>
              <p className="real-time__restart-dialog-text">
                A new session has started. We&apos;re loading your new session now.
              </p>
              <p className="real-time__restart-dialog-text">
                You can export your current transcript or continue without exporting.
              </p>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {sessionNotificationType === 'session_resumed' ? (
            <Button onClick={handleRestartContinue} variant="contained">
              Reload
            </Button>
          ) : (
            <>
              <Button onClick={handleRestartExport} variant="outlined">
                Export
              </Button>
              <Button onClick={handleRestartContinue} variant="contained">
                Continue
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Footer theme={themeClassName} />
    </div>
  );
};

export default withAuth(RealTime, { forceLogin: true });
